import { type MouseEvent } from 'react';
import { ReactComponent as CircleCheck } from '@assets/icons/circle-check.svg';
import { ReactComponent as InfoIcon } from '@assets/icons/info-light-circle.svg';
import config from 'config';
import { useShowSvcPrice } from 'context/ShowSvcPriceContext';
import parse from 'html-react-parser';

import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useTheme
} from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';

import {
  convertDuration,
  formatCurrency,
  getAllowServiceToCheck,
  getTooltipLabelServcie
} from 'utils';

import { OnlineBookingMeth, SuggestedService } from 'types';

import { Divider } from 'ui';
import { InfoModal } from '../../../ServiceList/components/InfoModal';

const {
  login: { desktop_banners }
} = config;

interface SuggestedServiceItemProps {
  suggestion: SuggestedService;
  isChecked: boolean;
  isDisabled: boolean;
  isLastItem: boolean;
  onToggle: () => void;
}

const SuggestedServiceItem = ({
  suggestion,
  isChecked,
  isDisabled,
  isLastItem,
  onToggle
}: SuggestedServiceItemProps) => {
  const {
    colors: {
      palette: {
        booking: {
          stepService: {
            serviceList: { serviceItem }
          }
        }
      }
    }
  } = useTheme();

  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const { guestInfo } = useAppSelector((state) => state.persistGuestAuth);

  const isLogged = Boolean(userInfo?.clid) && Boolean(userInfo?.token);
  const isGuestLogged = Boolean(guestInfo?.clid) && Boolean(guestInfo?.token);
  const isAuthenticated = isLogged || isGuestLogged;

  const onlineBookingMeth =
    suggestion.online_booking_meth ?? OnlineBookingMeth.Everyone;
  const isAllowToCheck =
    !isDisabled && getAllowServiceToCheck(isAuthenticated, onlineBookingMeth);
  const tooltipLabel = getTooltipLabelServcie(
    isAuthenticated,
    onlineBookingMeth
  );

  const { isOpen, onOpen, onClose } = useDisclosure();
  const showSvcPrice = useShowSvcPrice();
  const itemPrice = suggestion.svc_price ?? suggestion.retail ?? 0;
  const durationLabel = suggestion.duration
    ? convertDuration(suggestion.duration)
    : '';
  const metaParts = [
    durationLabel,
    showSvcPrice && itemPrice > 0 ? formatCurrency(itemPrice) : ''
  ].filter(Boolean);
  const metaLine = metaParts.join(' | ');
  const modalTitle = (() => {
    if (metaLine) {
      return metaLine;
    }

    if (durationLabel) {
      return durationLabel;
    }

    if (showSvcPrice && itemPrice > 0) {
      return formatCurrency(itemPrice);
    }

    return '';
  })();

  const ButtonIcon = isChecked ? (
    <CircleCheck fill={serviceItem.checkedIconColor} width={25} height={25} />
  ) : (
    <Box
      w='100%'
      h='100%'
      border='1px solid'
      borderColor={serviceItem.uncheckedBorderColor}
      borderRadius='50%'
    />
  );

  const handleClickService = () => {
    if (isAllowToCheck) {
      onToggle();
    }
  };

  const handleMoreDetailsClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onOpen();
  };

  return (
    <Stack gap={0} w='100%' opacity={isAllowToCheck ? 1 : 0.5}>
      <Grid
        bg={serviceItem.background}
        templateColumns='repeat(8, 1fr) auto'
        columnGap='15px'
        py='20px'
        px={{ base: '15px', md: '20px' }}
      >
        <GridItem colSpan={8}>
          <Flex
            alignItems='center'
            h='100%'
            gap='15px'
            flexShrink={1}
            onClick={handleClickService}
            cursor={isAllowToCheck ? 'pointer' : 'default'}
          >
            {isAllowToCheck ? (
              <Box w='25px' h='25px' minW='25px' bg='transparent'>
                {ButtonIcon}
              </Box>
            ) : (
              <Tooltip
                label={tooltipLabel}
                placement='bottom-start'
                shouldWrapChildren
                py='15px'
                px='12px'
                width='210px'
                bg='black'
                color='white'
                borderRadius='2px'
                top={-1}
                left={2}
              >
                <Box w='25px' h='25px' minW='25px' bg='transparent'>
                  {ButtonIcon}
                </Box>
              </Tooltip>
            )}

            <Stack gap='5px'>
              <Text
                fontWeight='semibold'
                fontSize={{ base: '18px', md: '22px' }}
              >
                {parse(suggestion.item_name)}
              </Text>
              {metaLine && (
                <Text fontSize='14px' mt='5px'>
                  {metaLine}
                </Text>
              )}
            </Stack>
          </Flex>
        </GridItem>

        <GridItem colSpan={1}>
          <Flex
            alignItems='center'
            justifyContent='flex-end'
            h='100%'
            gap='16px'
            mr='-7px'
          >
            <Button
              flexShrink={0}
              variant='text'
              onClick={(event) => handleMoreDetailsClick(event)}
              _hover={{
                '& p, & svg': {
                  color: serviceItem.hoverTextColor,
                  fill: serviceItem.hoverIconColor
                }
              }}
            >
              <Text
                fontSize='12px'
                color={serviceItem.textColor}
                mr='10px'
                display={{ base: 'none', sm: 'block' }}
              >
                More Details
              </Text>
              <InfoIcon width={18} height={18} fill={serviceItem.iconColor} />
            </Button>
          </Flex>
        </GridItem>
      </Grid>

      {!isLastItem && (
        <Divider w='100%' borderColor={serviceItem.dividerColor} />
      )}

      <InfoModal
        isOpen={isOpen}
        onClose={onClose}
        subtitle={suggestion.item_name}
        title={modalTitle}
        description={suggestion.dscr ?? ''}
        banners={desktop_banners}
      />
    </Stack>
  );
};

export default SuggestedServiceItem;
